// src/components/ReferralBadge.tsx
// Programa Embajadora/es: banner sutil + modal "¿cómo funciona?".
// Autocontenido: busca el código propio en /api/referrals/me (se genera
// solo si el usuario aún no tiene). Reusable en cualquier panel.

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Gift, Copy, Check, X, MessageCircle, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const COMMISSION_LABEL = '$5.000';

// Preferencia "ocultar banner" (si localStorage no persiste, solo vuelve a
// mostrarse el banner: molestia mínima y re-ocultable con un click).
const COLLAPSED_KEY = 'consultar_referral_badge_collapsed';

interface ReferralInfo {
  referral_code: string | null;
  referidos_total: number;
  referidos_pagando: number;
  referidos_en_trial: number;
}

const ReferralBadge: React.FC<{ className?: string }> = ({ className = '' }) => {
  const [info, setInfo] = useState<ReferralInfo | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  // Punto de montaje del ícono colapsado en el header del panel (si el panel
  // lo ofrece). Se resuelve post-mount porque el ancla es un nodo hermano que
  // puede no existir aún durante el primer render.
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setAnchor(document.getElementById('referral-badge-anchor'));
  }, []);

  const collapse = () => {
    try { localStorage.setItem(COLLAPSED_KEY, '1'); } catch { /* noop */ }
    setCollapsed(true);
  };

  const expand = () => {
    try { localStorage.removeItem(COLLAPSED_KEY); } catch { /* noop */ }
    setCollapsed(false);
  };

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/api/referrals/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (!cancelled && json?.success) setInfo(json.data);
      })
      .catch(() => {
        /* sin código no se muestra el banner; no molestar */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Sin datos o sin código → no renderizar nada (banner opcional por diseño)
  if (!info?.referral_code) return null;

  // Oculto → ícono discreto que lo restaura.
  // Preferencia: anclado en el header del panel (span#referral-badge-anchor,
  // al lado del widget de clima). Fallback sin ancla: burbuja flotante alta
  // (bottom-24) para no solaparse con la burbuja de Soporte/WhatsApp.
  if (collapsed) {
    if (anchor) {
      return createPortal(
        <button
          onClick={expand}
          title={`Programa Embajadora/es: ganá ${COMMISSION_LABEL}/mes por referido`}
          aria-label="Mostrar Programa Embajadora/es"
          className="relative ml-2 w-8 h-8 rounded-lg bg-violet-100 text-violet-600 hover:bg-violet-200 hover:scale-105 transition-all flex items-center justify-center"
        >
          <Gift className="w-4 h-4" />
          {info.referidos_total > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-0.5 rounded-full bg-violet-600 text-white text-[9px] font-bold flex items-center justify-center shadow-sm">
              {info.referidos_total}
            </span>
          )}
        </button>,
        anchor
      );
    }
    return createPortal(
      <button
        onClick={expand}
        title={`Programa Embajadora/es: ganá ${COMMISSION_LABEL}/mes por referido`}
        aria-label="Mostrar Programa Embajadora/es"
        className="fixed bottom-24 right-5 z-[150] w-12 h-12 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg hover:shadow-xl hover:scale-110 transition-all flex items-center justify-center"
      >
        <Gift className="w-5 h-5" />
        {info.referidos_total > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-white text-violet-700 text-[10px] font-bold flex items-center justify-center border border-violet-200 shadow-sm">
            {info.referidos_total}
          </span>
        )}
      </button>,
      document.body
    );
  }

  const referralLink = `${window.location.origin}/register?ref=${info.referral_code}`;

  const waText = encodeURIComponent(
    `Hola! Te comparto ConsultAR, el sistema de agenda y turnos que uso para gestionar consultorios. ` +
      `Con este link te registrás y probás gratis 15 días: ${referralLink}`
  );
  const waShareUrl = `https://wa.me/?text=${waText}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
    } catch {
      // Fallback para navegadores/webviews sin clipboard API
      const ta = document.createElement('textarea');
      ta.value = referralLink;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  return (
    <>
      {/* Banner sutil */}
      <div
        className={`flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 to-fuchsia-50 px-4 py-3 ${className}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
            <Gift className="w-4 h-4 text-violet-600" />
          </span>
          <p className="text-sm text-gray-700 leading-snug">
            <span className="font-semibold text-violet-700">Ganá {COMMISSION_LABEL} por mes</span>{' '}
            por cada profesional que traigas a ConsultAR
          </p>
        </div>

        {info.referidos_total > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-violet-200 text-xs font-medium text-violet-700">
            <Users className="w-3 h-3" />
            {info.referidos_total} {info.referidos_total === 1 ? 'referido' : 'referidos'}
            {info.referidos_pagando > 0 && ` · ${info.referidos_pagando} pagando`}
          </span>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <button
            onClick={copyLink}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? '¡Copiado!' : 'Copiar mi link'}
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="text-xs font-medium text-violet-700 hover:text-violet-900 underline underline-offset-2"
          >
            Más info
          </button>
          <button
            onClick={collapse}
            title="Ocultar (queda como ícono arriba, junto al clima)"
            aria-label="Ocultar Programa Embajadora/es"
            className="p-1.5 rounded-lg text-violet-400 hover:text-violet-700 hover:bg-violet-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Modal "¿cómo funciona?" */}
      {showModal &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowModal(false)}
            />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="relative bg-gradient-to-r from-violet-500 to-fuchsia-500 px-6 py-5 text-white">
                <button
                  onClick={() => setShowModal(false)}
                  className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                  aria-label="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white/20 text-[11px] font-bold uppercase tracking-wide mb-2">
                  <Gift className="w-3 h-3" /> Programa Embajadora/es
                </span>
                <h3 className="text-xl font-bold leading-tight">
                  Ganá {COMMISSION_LABEL} por mes, por cada profesional
                </h3>
              </div>

              <div className="px-6 py-5">
                <ol className="space-y-3 mb-5">
                  {[
                    'Compartí tu link personal con profesionales que conozcas (médicos, kinesiólogas, esteticistas…).',
                    'Cuando se registran con tu link, quedan vinculados a vos automáticamente.',
                    `Cuando activan su suscripción, cobrás ${COMMISSION_LABEL} POR MES mientras sigan usando ConsultAR. Sin límite de referidos.`,
                  ].map((paso, i) => (
                    <li key={i} className="flex gap-3 text-sm text-gray-700">
                      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-100 text-violet-700 font-bold text-xs flex items-center justify-center">
                        {i + 1}
                      </span>
                      <span className="leading-snug">{paso}</span>
                    </li>
                  ))}
                </ol>

                <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 mb-4">
                  <p className="text-[11px] uppercase tracking-wide font-semibold text-gray-400 mb-1">
                    Tu link personal
                  </p>
                  <p className="text-sm font-mono text-gray-800 break-all">{referralLink}</p>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={copyLink}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? '¡Copiado!' : 'Copiar link'}
                  </button>
                  <a
                    href={waShareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" />
                    Compartir
                  </a>
                </div>

                <p className="text-[11px] text-gray-400 mt-3 leading-snug">
                  La comisión se acredita por transferencia una vez por mes, por cada referido con
                  suscripción activa ese mes.
                </p>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default ReferralBadge;
